import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { ensureSampleData } from '../../state/actions';
import {
  selectIsStudySaved,
  selectReportContentItemsSlice,
  selectSampleDataAdjusted,
  selectSampleDataOriginal,
  selectSampleDataStatus,
  selectStudy
} from '../../state/selectors';
import { SampleCalcs } from '../../../shared/models/calculate.model';
import { SharedTable } from '../../../shared/models/table.model';
import { REPORT_ITEM_COLUMN_KEYS, reportItemColumnValues, sortReportContentItems, withReportItemColumns } from '../../../shared/utils/report-item-columns.util';
import { RerunSummary, rerunSamples, summarizeReruns } from '../../../shared/utils/rerun-samples.util';
import { mapPlainRows } from '../../../shared/utils/sample-calc-rows.util';
import { finalSampleData } from '../../../shared/utils/helper';
import { isCalAdjustFinal } from '../../../shared/utils/run-type.util';

/** Per-replicate columns only: the sample-level averages stay on the Calculations tab. */
const RERUN_COLUMNS: Record<string, string> = {
  accessionId: 'Accession',
  repNum: 'Original Replicate ID',
  updatedRepNum: 'Updated Replicate ID',
  beadCV: 'Bead CV',
  beadCount: 'Bead Count',
  countTime: 'Count Time',
  rfi: 'RFI',
  normalizingBeadSignal: 'Normalizing Bead Signal',
  calcFR: 'Calculated FR or RFI',
  reqNumReplicates: 'Num Replicates',
  instrumentConc: 'Instrument AI',
  calculatedConc: 'Calculated AI',
  finalValue: 'Final Value (Calculated)',
  finalRepCount: 'Final Value Replicate Count',
  repCountOk: 'Replicate Count OK',
  discardReason: 'Discard Reason'
};

/** Replicates of samples whose instrument replicate numbering restarted (reruns), across all report items. */
@Component({
  selector: 'app-rerun-samples',
  templateUrl: './rerun-samples.component.html',
  styleUrl: './rerun-samples.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RerunSamplesComponent implements OnDestroy {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  // Stable references: a fresh array per change detection would reset the table's column filters.
  readonly dropdownFilterKeys = ['accessionId', ...REPORT_ITEM_COLUMN_KEYS];
  readonly customCssClassBinding = (): { [cssClass: string]: boolean } => ({});

  private destroy$ = new Subject<void>();

  /* eslint-disable @ngrx/avoid-combining-selectors -- Individual slices keep OnPush checks cheap. */
  vm$ = combineLatest([
    this.store.select(selectStudy),
    this.store.select(selectReportContentItemsSlice),
    this.store.select(selectSampleDataOriginal),
    this.store.select(selectSampleDataAdjusted)
  ]).pipe(
    map(([study, reportContent, original, adjusted]) => {
      const samples = isCalAdjustFinal(study) ? finalSampleData(adjusted, original) : original;
      const items = sortReportContentItems(reportContent?.reportContentItems ?? [], study?.refKitLot ?? '');
      const rows: SampleCalcs[] = items.flatMap(item => {
        const itemSamples = rerunSamples(samples.filter(sample => sample.reportContentItemId === item.reportContentItemId))
          .sort((a, b) => (a.accessionNo ?? '').localeCompare(b.accessionNo ?? ''));
        const itemColumns = reportItemColumnValues(item);
        return mapPlainRows(itemSamples).map(row => ({ ...row, reportContentItemId: item.reportContentItemId, ...itemColumns }));
      });
      const summary: RerunSummary = summarizeReruns(rows);
      return {
        hasCalculation: !!(study?.calculationResponse ?? study?.calculationAdjustedResponse),
        summary,
        tableContent: new SharedTable(withReportItemColumns(RERUN_COLUMNS), rows)
      };
    })
  );
  /* eslint-enable @ngrx/avoid-combining-selectors */

  constructor(private readonly store: Store) {
    this.monitorSampleDataStatus();
  }

  /** Pulls sample data when a calculation has made it stale; Run Status does not otherwise load it. */
  private monitorSampleDataStatus(): void {
    this.store.select(selectSampleDataStatus)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(status => {
        if (!status.sampleDataFresh && !status.sampleDataLoading) {
          this.store.dispatch(ensureSampleData());
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
